import styled from '@emotion/styled';
import { ReviewWriteForm } from './ReviewWriteForm';
import ChevronIcon from '../../../assets/icons/ChevronIcon';
import { CompleteView } from './common/CompleteView';
import BottomSheet from './common/BottomSheet';
import { COLORS, FONT_SIZES, FONT_WEIGHTS, SPACING, Z_INDEX, BREAKPOINTS } from './common/constants';
import { useReviewForm } from '../hooks/useReviewForm';
import { useReviewModalState } from '../hooks/useReviewModalState';
import { useReviewSubmission } from '../hooks/useReviewSubmission';
import type { ReviewModalProps } from './common/interfaces';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${Z_INDEX.mobileModal};
  background: ${COLORS.background};
  display: flex;
  flex-direction: column;
  @media (min-width: ${BREAKPOINTS.mobile}) {
    display: none;
  }
`;

const Header = styled.div`
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  background: ${COLORS.background};
  padding: ${SPACING.xl} ${SPACING.lg} ${SPACING.sm} ${SPACING.lg};
  box-sizing: border-box;
  gap: ${SPACING.lg};
`;

const Title = styled.div`
  color: ${COLORS.text};
  font-feature-settings: 'ss10' on;
  font-family: 'Plus Jakarta Sans';
  font-size: ${FONT_SIZES['2xl']};
  font-style: normal;
  font-weight: ${FONT_WEIGHTS.normal};
  line-height: 140%;
`;

const CloseButton = styled.button`
  border: none !important;
  font-size: 28px !important;
  color: #333 !important;
  cursor: pointer !important;
  border-radius: 30px;
  padding: ${SPACING.sm};
  background: revert !important;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background: ${COLORS.background};
  padding: ${SPACING.lg};
`;

export const ReviewWriteMobileModal = ({ isOpen, productImage, onClose, onSubmit }: ReviewModalProps) => {
  const { formState, formActions, formOptions, isSubmitDisabled, resetForm } = useReviewForm({ isOpen });

  const { isComplete, setIsComplete, handleClose } = useReviewModalState({
    isOpen,
    onClose: () => {
      resetForm();
      onClose();
    },
    hasUnsavedChanges: false,
    enableConfirmation: false,
  });

  const { handleSubmit } = useReviewSubmission({
    formData: formState,
    onSubmit,
    onSubmissionComplete: () => setIsComplete(true),
  });

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!isComplete && (
        <Overlay>
          <Header>
            <CloseButton onClick={handleClose}>
              <ChevronIcon direction="left" />
            </CloseButton>
            <Title>Review this product</Title>
          </Header>

          <ScrollArea>
            <ReviewWriteForm
              formData={formState}
              formActions={formActions}
              options={formOptions}
              productImage={productImage}
              isSubmitDisabled={isSubmitDisabled}
              onSubmit={handleSubmit}
              variant="mobile"
            />
          </ScrollArea>
        </Overlay>
      )}

      {isComplete && (
        <BottomSheet isOpen={isComplete} onClose={onClose} title="">
          <CompleteView onContinue={onClose} variant="mobile" />
        </BottomSheet>
      )}
    </>
  );
};
